<script lang="ts">
    import type {CalendarView} from 'shared/components/kp-calendar/types';
    import {KpCalendarManager} from 'shared/components/kp-calendar/kp-calendar-manager';
    import {createEventDispatcher, onDestroy} from 'svelte';
    import {DateTime} from 'luxon';
    import {fly} from 'svelte/transition';
    import {unsubscribeAllSubscriptions} from 'shared/utils/observables-utils';
    import {getCurrentLanguage} from 'core/svelte-context/context';
    import {exists} from 'shared/utils/custom-utils';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';
    import KpVerticalSeparator from 'shared/ui-widgets/separator/KpVerticalSeparator.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import {
        calendarDateFromLuxon,
        getPaddedMonthDays,
        luxonDateFromCalendarDate
    } from 'shared/components/kp-calendar/utils';

    const dispatch = createEventDispatcher<{'date-selected': DateTime}>();

    const calendarManager = KpCalendarManager.createNavigableCalendar({
        type: 'monthView',
        startDate: calendarDateFromLuxon(DateTime.now())
    });

    let calendarView: CalendarView;
    $: paddedDays = getPaddedMonthDays(calendarView.days);
    const subscription = calendarManager.getView$().subscribe((view) => calendarView = view);
    const dayNames = ['PO', 'ÚT', 'ST', 'ČT', 'PÁ', 'SO', 'NE'];

    onDestroy(() => {
        unsubscribeAllSubscriptions(subscription);
    });

    function getCalendarLabel(view: CalendarView): string {
        if (exists(view.month)) {
            const formatter = new Intl.DateTimeFormat(getCurrentLanguage(), {month: 'long'});
            const monthDate = new Date(view.year, view.month - 1, 1);
            return `${view.year} - ${formatter.format(monthDate)}`;
        }

        if (exists(view.week)) {
            return `${view.week}. týden`;
        }

        return '';
    }
</script>

<div class="date-selector-calendar-container">
    <div class="toolbar-row">
        <KpButton buttonSize="md" on:click={() => calendarManager.navigateToToday()}>Dnes</KpButton>
        <KpIconButton buttonSize="md" on:click={() => calendarManager.navigate(-1)} icon="angle-small-left"/>
        <KpIconButton buttonSize="md" on:click={() => calendarManager.navigate(1)} icon="angle-small-right"/>
        <KpVerticalSeparator height="16px"/>
        <span>{getCalendarLabel(calendarView)}</span>
    </div>

    <div class="grid-calendar">
        {#key calendarView}
            <div class="grid-calendar-inner"
                 style:--rows="{paddedDays.length / 7}"
                 in:fly="{{x: 20 * calendarView.navigateDirection, duration: 300}}">

                {#each paddedDays as day, index}
                    <button class="calendar-day"
                            on:click={() => dispatch('date-selected', luxonDateFromCalendarDate(day))}
                            class:current-day={day.current}
                            class:padding-day={day.padding}>

                        {#if index < 7}
                            <span class="day-name">{dayNames[index]}</span>
                        {/if}

                        <span class="date">{day.day}</span>
                    </button>
                {/each}
            </div>
        {/key}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro-erp.less";

    .date-selector-calendar-container {
        .flex-grow();
        gap: @spacing-ml;

        .toolbar-row {
            display: flex;
            align-items: center;
            gap: @spacing-sm;

            span {
                font-weight: 500;
                font-size: @font-size-large;
            }
        }

        .grid-calendar {
            .flex-grow();
            border: 1px solid @themed-border-default;
            border-radius: @border-radius-large;
            overflow: hidden;

            .grid-calendar-inner {
                flex: 1;
                width: 100%;
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                grid-template-rows: repeat(var(--rows), 1fr);
                gap: 1px;
                background-color: @themed-border-default;
            }

            .calendar-day {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: @spacing-sm;
                background-color: @themed-body-bg;
                cursor: pointer;
                outline: none;
                border: 2px solid transparent;
                transition: border-color ease-in-out 0.3s;

                &:hover {
                    border-color: var(--accent-blue-new);
                }

                &.current-day {
                    background-color: @themed-body-bg-blue-highlighted;

                    &:before {
                        content: '';
                        position: absolute;
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background-color: var(--accent-blue-new);
                        top: @spacing-m;
                        right: @spacing-m;
                    }
                }

                &.padding-day {
                    opacity: 0.85;
                }

                .day-name,
                .date {
                    font-size: @font-size-small;
                }

                .day-name {
                    color: @themed-text-muted;
                }

                .date {
                    font-weight: 500;
                }
            }
        }
    }
</style>